import { BackupIcon } from '@app/components/icons/backup';
import { EjectIcon } from '@app/components/icons/eject';
import { RehearsalIcon } from '@app/components/icons/rehearsal';
import { SignalStreamIcon } from '@app/components/icons/signal-stream';
import { StudioRecordingIcon } from '@app/components/icons/studio-recording';
import { VideoPlayIcon } from '@app/components/icons/video-play';
import { useFlag } from '@app/feature-flags/use-flag';
import { Recording } from '@app/routes/content/domain';
import { FLAG_ENABLE_STANDALONE_STUDIO } from '@app/utilities/flags';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { SVGProps } from 'react';

import { LibraryUpload } from '../routes/library/components/icons';

type SourceConfig = {
  icon: React.ComponentType<SVGProps<SVGSVGElement>>;
  translationKey: string;
};

const getSourceConfig = (
  recording: Recording,
  isStandaloneStudioEnabled: boolean,
): SourceConfig => {
  const { category, is_original, event_id } = recording;

  if (category === 'upload' && event_id === null) {
    return {
      icon: LibraryUpload,
      translationKey: 'content-hub.library-upload',
    };
  }

  if (isStandaloneStudioEnabled && category === 'main' && event_id === null) {
    return {
      icon: StudioRecordingIcon,
      translationKey: 'content-hub.studio-recording',
    };
  }

  if (!is_original) {
    return {
      icon: VideoPlayIcon,
      translationKey: 'content-hub.clip',
    };
  }

  const baseConfigs: Record<Recording['category'], SourceConfig> = {
    upload: {
      icon: EjectIcon,
      translationKey: 'content-hub.my-upload',
    },
    backup: {
      icon: BackupIcon,
      translationKey: 'content-hub.backup',
    },
    rehearsal: {
      icon: RehearsalIcon,
      translationKey: 'content-hub.rehearsal',
    },
    default: {
      icon: SignalStreamIcon,
      translationKey: 'content-hub.default',
    },
    main: {
      icon: SignalStreamIcon,
      translationKey: 'content-hub.recording',
    },
  };

  return baseConfigs[category];
};

export const SourceTag = ({
  recording,
  className,
}: {
  recording: Recording;
  className?: string;
}) => {
  const { t } = useTranslation();
  const isStandaloneStudioEnabled = useFlag(FLAG_ENABLE_STANDALONE_STUDIO);
  const { icon: IconComponent, translationKey } = getSourceConfig(
    recording,
    Boolean(isStandaloneStudioEnabled),
  );

  return (
    <div className={className}>
      <IconComponent className="size-4" /> {t(translationKey)}
    </div>
  );
};
